import styled from 'styled-components';
import getColour, { ValidHex } from '@lendi-ui/commons/colours';
import { m, mb, ml, p, px, py } from '@lendi-ui/spacing';
import { select } from '@lendi-ui/theme';
import { body, link } from '@lendi-ui/typography';

import { materialCard } from '../style';

interface TableContainerProps {
  minWidth: number;
  headerSizes: { widthRatio: number; minWidth: number }[];
  isSticky?: boolean;
}

interface ButtonProps {
  color?: ValidHex;
}

export const TableWrapper = styled.div`
  ${mb('md')}
  ${materialCard};
`;

export const TableTitle = styled.div`
  position: relative;
  background-color: ${() => getColour('fill', 'contrast')};
  ${body({ size: 'md' })};
  ${py('xxs')};
  ${px('sm')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const TableTitleText = styled.div`
  color: ${() => getColour('text', 'inverse')};
`;

export const TableContainer = styled.div<TableContainerProps>`
  ${body()};
  overflow-x: auto;

  table {
    width: 100%;
    border: none;
    border-spacing: 0;
    ${m('nil')};
    ${p('nil')};
    ${(props) => `
      min-width: ${props.minWidth}px;
    `};

    tr {
      display: flex;
    }

    td {
      display: flex;
      align-items: center;
    }
  }

  thead {
    background-color: ${() => getColour('shade', 50)};

    th {
      ${body({ size: 'xs' })};
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background-color: ${() => getColour('shade', 50)};
      }

      &:nth-child(odd) {
        background-color: ${() => getColour('shade', 0)};
      }

      td {
        ${body({ size: 'sm' })};
      }

      &:last-child {
        border-bottom-left-radius: ${select('borderRadius')};
        border-bottom-right-radius: ${select('borderRadius')};
      }
    }

    tr.sub-heading td {
      ${body({ size: 'xs' })};
      font-weight: 700;
      width: 100% !important;
    }
  }

  thead tr th,
  tbody tr td {
    padding: 0 10px;

    &.right-aligned {
      text-align: right;
      justify-content: flex-end;
      margin-left: auto;
    }

    ${py('xs')}
    ${(props) =>
      props.headerSizes.reduce(
        (str, size, index) => `
          ${str}
          &:nth-child(${index + 1}) {
            width: ${size.widthRatio * 100}%;
            min-width: ${size.minWidth}px;
          }
        `,
        ''
      )};

    ${(props) =>
      props.isSticky &&
      `
        &:nth-child(1) {
          left: 0px;
          position: sticky;
        }
    `};
  }

  tfoot tr td {
    width: 100%;
    ${p('sm')};
    ${(props) =>
      props.isSticky &&
      `
        &:nth-child(1) {
          left: 0px;
          height: 100%;
          position: sticky;
        }
    `};
  }
`;

export const TableCommentsButton = styled.button<ButtonProps>`
  ${link({ size: 'xs' }) as {}};
  color: ${(props) => props.color || getColour('interaction', 'subtle')};
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 1px;
  text-decoration: none;
  text-transform: uppercase;

  &:hover,
  &:focus,
  &:active {
    border: none;
    outline: none;
    text-decoration: none;
  }
`;

export const TableCommentsIcon = styled.div`
  display: inline-block;
  position: relative;
  top: -1px;
  ${ml('xxs')};
`;
